<template>
  <div class="w-full flex justify-center" style="container-type: inline-size;">
    <div v-if="loadingInitialData" class="w-full flex justify-center items-center fade-in" style="max-width: 1104px; height: calc(100vh - 250px);">
      <div class="flex flex-col items-center">
        <BaseLoadingLogo noPadding :margin="0" :width="48" :height="48" />
        <BaseText type="label" size="sm" class="text-text-subdued mt-5">
          Loading your listing details
        </BaseText>
        <BaseText type="body" size="sm" class="text-text-disabled mt-1">
          Hang tight...
        </BaseText>
      </div>
    </div>
    <transition name="fade" appear>
      <div v-if="!loadingInitialData" class="w-full flex flex-col fade-in" style="max-width: 1104px;">
        <!-- AGENCY DETAILS SECTION -->
        <div class="listing-section w-full p-7 border-b border-border-normal">
          <!-- Section Label -->
          <div class="flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Agency Details
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              Details about your agency.
            </BaseText>
          </div>
          <!-- Form -->
          <div class="w-full flex flex-col">
            <!-- Avatar Editor -->
            <div class="w-full flex gap-5">
              <img v-if="editingListing.avatar" :src="editingListing.avatar" alt="Agency Avatar" class="w-11 h-11 rounded-lg" />
              <div v-else class="w-11 h-11 rounded-lg bg-neutral-25" />
              <div class="flex-grow flex flex-col gap-4">
                <div class="flex flex-col gap-1">
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Logo
                  </BaseText>
                  <BaseText type="body" size="sm" class="text-text-normal">
                    We support PNGs and JPEGs.
                  </BaseText>
                </div>
                <div class="flex items-center gap-2">
                  <button class="secondary-btn flex items-center gap-1.5 rounded-md pl-1.5 py-1.5 pr-2.5" :disabled="loadingAvatarUpload"
                  @click="handleFileInput">
                    <input ref="fileInput" class="hidden" type="file" @change="handleFileUpload" />
                    <BaseLoadingSpinnerCircle v-if="loadingAvatarUpload" :duration="1.2" class="text-icon-normal" />
                    <UploadIcon v-else class="text-icon-normal" />
                    <BaseText type="label" size="sm" class="text-text-muted">
                      Upload Logo
                    </BaseText>
                  </button>
                  <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
                  @click="handleRemoveAvatar">
                    <BaseText type="label" size="sm" class="text-text-muted">
                      Remove
                    </BaseText>
                  </button>
                </div>
                <div class="flex gap-2" style="max-width: 320px">
                  <InfoIcon class="text-icon-normal flex-shrink-0" />
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Choose a square image with a background for best appearance.
                  </BaseText>
                </div>
              </div>
            </div>
            <!-- Field Inputs -->
            <div class="w-full flex flex-col gap-5 mt-5">
              <!-- Agency Name -->
              <BaseInputV3
                label="Agency Name"
                :inputValue="editingListing.agencyName"
                :required="isFieldRequired('agencyName')"
                :isValid="fieldIsValid('agencyName')"
                :errorMessage="customValidations?.agencyName?.message ?? ''"
                @update:inputValue="handleInput('agencyName', $event)"
                placeholder="Your Agency"
              />
              <!-- Agency Website -->
              <BaseInputV3
                label="Website"
                :inputValue="editingListing.agencyWebsite"
                :required="isFieldRequired('agencyWebsite')"
                :isValid="fieldIsValid('agencyWebsite')"
                :errorMessage="customValidations?.agencyWebsite?.message ?? ''"
                @update:inputValue="handleInput('agencyWebsite', $event)"
                placeholder="https://www.youragency.com"
              />
              <!-- Agency LinkedIn Profile -->
              <BaseInputV3
                label="LinkedIn URL"
                :inputValue="editingListing.agencyLinkedInProfile"
                :required="isFieldRequired('agencyLinkedInProfile')"
                :isValid="fieldIsValid('agencyLinkedInProfile')"
                :errorMessage="customValidations?.agencyLinkedInProfile?.message ?? ''"
                @update:inputValue="handleInput('agencyLinkedInProfile', $event)"
                placeholder="https://www.linkedin.com/company/your-agency"
              />
              <!-- Country Selector -->
              <div class="w-full flex flex-col gap-1">
                <div class="flex items-center gap-1.5">
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Primary Country
                  </BaseText>
                  <BaseText type="body" size="xs" class="text-text-subdued">
                    (required)
                  </BaseText>
                </div>
                <div class="relative w-full" style="z-index: 100">
                  <BaseSingleDropdown 
                    :options="locationOptions"
                    option-key="id"
                    :selected-obj="selectedCountry"
                    :selected-label="(opt) => `${opt.name}`"
                    :option-label="(opt) => opt.name"
                    use-parent-width
                    enable-search
                    search-key="name"
                    placeholder="Select Country"
                    search-placeholder="Search Countries"
                    :max-height="328"
                    @change="handleCountryChange"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- LISTING DESCRIPTION SECTION -->
        <div class="listing-section w-full p-7 border-b border-border-normal">
          <!-- Section Label / Actions -->
          <div class="flex flex-col">
            <BaseText type="label" size="sm" class="text-text-muted">
              Listing Description
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal mt-1" style="max-width: 420px;">
              We generated a description for you based on your website and LinkedIn content. Feel free to edit and refine before publishing.
            </BaseText>
          </div>
          <!-- Description Previews -->
          <div class="w-full flex flex-col gap-8">
            <div v-for="description in editableDescriptions" :key="`description-${description.key}`"
            class="w-full flex flex-col gap-3">
              <BaseText type="label" size="lg" class="text-text-muted">
                {{ description.header }}
              </BaseText>
              <AIDescriptionPreview :htmlContent="description.content" :isCustom="customDescription[description.key]" />
              <button class="secondary-btn flex items-center gap-1.5 rounded-md pl-1.5 py-1.5 pr-2.5 w-max"
              @click="editingDescription = description">
                <EditTextIcon class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  Edit Section
                </BaseText>
              </button>
            </div>
          </div>
        </div>
        <!-- SERVICE OFFERINGS SECTION -->
        <div class="listing-section w-full p-7">
          <!-- Section Label -->
          <div class="flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Core Service Offerings
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              Let prospects know what core service offerings your agency excels at.
            </BaseText>
          </div>
          <!-- Checkboxes -->
          <div class="w-full flex flex-col gap-4">
            <div v-for="service in servicesList" :key="`service-offering-${service.id}`"
            class="flex items-center gap-2" style="height: 24px;">
              <input 
                type="checkbox"
                class="checkbox-input rounded text-black cursor-pointer focus:ring-transparent transition-colors"
                :checked="service.selected"
                @click="handleCheckboxMultiSelect('services', service.id)"
              />
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ service.name }}
              </BaseText>
            </div>
          </div>
        </div>
        <!-- INDUSTRY SPECIALIZATIONS SECTION -->
        <div class="listing-section w-full p-7 border-b border-border-normal">
          <!-- Section Label -->
          <div class="flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Core Industries
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal" style="max-width: 420px;">
              Let prospects know what primary industries and niches you provide services for.
            </BaseText>
          </div>
          <!-- Checkboxes -->
          <div class="w-full flex flex-col gap-4">
            <div v-for="industry in industriesList" :key="`industry-${industry.id}`"
            class="flex items-center gap-2" style="height: 24px;">
              <input 
                type="checkbox"
                class="checkbox-input rounded text-black cursor-pointer focus:ring-transparent transition-colors"
                :checked="industry.selected"
                @click="handleCheckboxMultiSelect('industries', industry.id)"
              />
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ industry.name }}
              </BaseText>
            </div>
          </div>
        </div>
        <!-- LEAD INTRO EMAIL SECTION -->
        <div class="listing-section w-full p-7">
          <!-- Section Label -->
          <div class="flex flex-col gap-1">
            <BaseText type="label" size="sm" class="text-text-muted">
              Lead Details
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal">
              Let us know where to intro leads from the directory to.
            </BaseText>
          </div>
          <!-- Email Input -->
          <div class="w-full">
            <BaseInputV3
              label="Email"
              :inputValue="editingListing.leadIntroEmail"
              :required="isFieldRequired('leadIntroEmail')"
              :isValid="fieldIsValid('leadIntroEmail')"
              :errorMessage="customValidations?.leadIntroEmail?.message ?? ''"
              @update:inputValue="handleInput('leadIntroEmail', $event)"
              placeholder="sales@youragency.com"
            />
          </div>
        </div>
        <!-- SUBMIT ACTIONS -->
        <div class="w-full flex items-center gap-4 justify-end p-7">
          <button class="px-2.5 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25"
          @click="$emit('cancel')">
            <BaseText type="label" size="sm" class="text-text-muted">
              Cancel
            </BaseText>
          </button>
          <button class="submit-btn px-2.5 py-1.5 rounded-md flex items-center justify-center" 
          :class="submitEnabled ? 'enabled' : 'disabled cursor-default'"
          :disabled="!submitEnabled" @click="handleSubmit" style="width: 205px;">
            <BaseLoadingSpinnerCircle v-if="loadingSubmit" :duration="1.2" class="text-icon-disabled" />
            <BaseText v-else type="label" size="sm" class="transition-colors duration-100" :class="submitEnabled ? 'text-white' : 'text-text-disabled'">
              Publish my Directory Profile
            </BaseText>
          </button>
        </div>
      </div>
    </transition>
    <EditListingDescriptionModal 
      v-if="editingDescription"
      :initialHtmlContent="editingDescription.content"
      @save="handleDescriptionSave(editingDescription.key, $event)"
      @close="editingDescription = null"
    />
  </div>
</template>

<script>
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'

import AIDescriptionPreview from './AIDescriptionPreview.vue'
import EditListingDescriptionModal from './EditListingDescriptionModal.vue'

import UploadIcon from '../../globals/Icons/UploadIcon.vue'
import InfoIcon from '../../globals/Icons/InfoSimpleIcon.vue'
import EditTextIcon from '../../globals/Icons/EditTextIcon.vue'
import AIGeneratedTextIcon from '../../globals/Icons/AIGeneratedTextIcon.vue'

const REQUIRED_FIELDS = [
  'agencyName',
  'agencyWebsite',
  'agencyLinkedInProfile',
  'primaryCountry',
  'leadIntroEmail',
  'aboutDescriptionHtml',
  'whatSetsThemApartHtml',
  'coreServicesHtml'
]

export default {
  name: 'EditDirectoryListing',
  components: {
    AIDescriptionPreview,
    EditListingDescriptionModal,
    UploadIcon,
    InfoIcon,
    EditTextIcon,
    AIGeneratedTextIcon
  },
  props: {
    generatedListing: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      editingListing: _.cloneDeep(this.generatedListing),
      editingDescription: null,
      submitAttempted: false,
      loadingSubmit: false,
      loadingInitialData: true,
      loadingAvatarUpload: false,
      customDescription: {
        aboutDescriptionHtml: false,
        whatSetsThemApartHtml: false,
        coreServicesHtml: false
      },

      // Form Option States
      locationOptions: [],
      coreServices: [],
      coreIndustries: []
    }
  },
  created () {
    this.customDescription.aboutDescriptionHtml = !this.generatedListing.aboutDescriptionHtml.length
    this.customDescription.whatSetsThemApartHtml = !this.generatedListing.whatSetsThemApartHtml.length
    this.customDescription.coreServicesHtml = !this.generatedListing.coreServicesHtml.length
  },
  async mounted () {
    // Fetch the core services and industries
    try {
      // const { services, industries } = await ForeplayAPI.Agency.getServicesAndIndustries()
      const [countries, serviceAndIndustries] = await Promise.all([
        ForeplayAPI.Agency.getCountries(),
        ForeplayAPI.Agency.getServicesAndIndustries()
      ])
      this.locationOptions = countries
      this.coreServices = serviceAndIndustries.services
      this.coreIndustries = serviceAndIndustries.industries

      // Take the country code returned by the AI and set it to the appropriate webflow CMS id, or none
      // If we're editing an existing listing, this is already handled
      if (!this.isEditingExistingListing) {
        if (this.editingListing.primaryCountry) {
          this.editingListing.primaryCountry = countries.find(opt => opt.iso === this.editingListing.primaryCountry)?.id ?? null
        } else {
          this.editingListing.primaryCountry = null
        }
      }

      this.loadingInitialData = false
    } catch (error) {
      this.$showAlert({
        message: 'Error fetching core services and industries',
        type: 'error'
      })
    }
  },
  computed: {
    isEditingExistingListing () {
      return !!(this.generatedListing?.webflowId?.length)
    },
    selectedCountry () {
      if (!this.editingListing?.primaryCountry?.length) return { id: null, name: 'Select Country' }
      return this.locationOptions.find(opt => opt.id === this.editingListing.primaryCountry) ?? { id: null, name: 'Select Country' }
    },
    editableDescriptions () {
      const aboutName = this.editingListing?.agencyName?.length
        ? `About ${this.editingListing.agencyName}`
        : 'About your agency'
      return [
        {
          header: aboutName,
          key: 'aboutDescriptionHtml',
          content: this.editingListing.aboutDescriptionHtml
        },
        {
          header: 'What sets them apart',
          key: 'whatSetsThemApartHtml',
          content: this.editingListing.whatSetsThemApartHtml
        },
        {
          header: 'Core Services & Offer',
          key: 'coreServicesHtml',
          content: this.editingListing.coreServicesHtml
        }
      ]
    },
    servicesList () {
      return this.coreServices.map(service => ({
        id: service.id,
        name: service.fieldData.name,
        selected: this.editingListing.services.includes(service.id)
      }))
    },
    industriesList () {
      return this.coreIndustries.map(industry => ({
        id: industry.id,
        name: industry.fieldData.name,
        selected: this.editingListing.industries.includes(industry.id)
      }))
    },
    submitEnabled () {
      return !this.loadingSubmit
    },
    customValidations () {
      const validateUrl = (value) => {
        const url = value.trim()
        if (!url.length) return false
        const re = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/
        return re.test(url)
      }
      const validateEmail = (value) => {
        const email = value.trim()
        if (!email.length) return false
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return re.test(email)
      }
      return {
        agencyName: {
          isValid: !!(this.editingListing.agencyName?.length),
          message: 'Enter a valid agency name'
        },
        agencyWebsite: {
          isValid: validateUrl(this.editingListing.agencyWebsite),
          message: 'Enter a valid Website URL'
        },
        agencyLinkedInProfile: {
          isValid: validateUrl(this.editingListing.agencyLinkedInProfile),
          message: 'Enter a valid LinkedIn URL'
        },
        primaryCountry: {
          isValid: !!(this.editingListing.primaryCountry?.length),
          message: 'Select a primary country from the dropdown'
        },
        leadIntroEmail: {
          isValid: validateEmail(this.editingListing.leadIntroEmail),
          message: 'Enter a valid lead intro email address'
        }
      }
    }
  },
  methods: {
    async handleSubmit () {
      this.submitAttempted = true
      const valid = this.runValidations()
      if (!valid) return

      // Submit the listing to the API
      this.loadingSubmit = true
      try {
        let firebaseId = null
        if (this.isEditingExistingListing) {
          firebaseId = await ForeplayAPI.Agency.updateAgencyListing(this.editingListing)
        } else {
          firebaseId = await ForeplayAPI.Agency.submitAgencyListing(this.editingListing)
        }
        const listingDoc = await FirebaseAPI.Agency.getListingById(firebaseId)
        this.$showAlert({
          type: 'success',
          message: `${this.isEditingExistingListing ? 'Listing updated successfully' : 'Listing submitted successfully'}`
        })
        this.$emit('listingSubmitted', listingDoc)
      } catch (error) {
        console.error('Error submitting listing: ', error)
        this.$showAlert({
          type: 'error',
          message: 'Error submitting listing'
        })
      } finally {
        this.loadingSubmit = false
      }
    },
    runValidations () {
      const customValidations = Object.values(this.customValidations)
      if (!customValidations.every(validation => validation.isValid)) {
        this.$showAlert({
          type: 'error',
          message: 'Please fix the errors in the form',
          timeout: 2000
        })
        customValidations.forEach(validation => {
          if (!validation.isValid) {
            this.$showAlert({
              type: 'error',
              message: validation.message
            })
          }
        })
        return false
      } else if (!this.editingListing.aboutDescriptionHtml.length || !this.editingListing.whatSetsThemApartHtml.length || !this.editingListing.coreServicesHtml.length) {
        this.$showAlert({
          type: 'error',
          message: 'Please fill in all description sections'
        })
        return false
      }
      return true
    },
    fieldIsValid (key) {
      return !this.submitAttempted || this.customValidations[key].isValid
    },
    handleInput (key, text) {
      this.editingListing[key] = text
    },
    handleCountryChange (countryOption) {
      this.editingListing.primaryCountry = countryOption?.id ?? null
    },
    handleCheckboxMultiSelect (key, id) {
      if (this.editingListing[key].includes(id)) {
        this.editingListing[key] = [...this.editingListing[key]].filter(itemId => itemId !== id)
      } else {
        this.editingListing[key].push(id)
      }
    },
    handleDescriptionSave (key, htmlContent) {
      this.editingListing[key] = htmlContent
      this.customDescription[key] = true
      this.editingDescription = null
    },
    isFieldRequired (key) {
      return REQUIRED_FIELDS.includes(key)
    },

    // ======= AVATAR UPLOAD FUNCTIONS =======
    handleFileInput () {
      this.$refs.fileInput.click()
    },
    async handleFileUpload (event) {
      const mediaFile = event.target.files[0]
      if (!mediaFile) return
      const fileType = mediaFile.type
      if (!(fileType === 'image/png' || fileType === 'image/jpeg')) {
        this.$showAlert({
          message: 'Only JPEG and PNG accepted',
          type: 'error'
        })
        return
      }

      this.loadingAvatarUpload = true
      try {
        const avatarUrl = await ForeplayAPI.Agency.uploadAgencyAvatar(mediaFile)
        this.editingListing.avatar = avatarUrl
      } catch (error) {
        console.error('Error uploading avatar: ', error)
        this.$showAlert({
          message: 'Error uploading avatar',
          type: 'error'
        })
      } finally {
        this.loadingAvatarUpload = false
        event.target.value = ''
      }
    },
    handleRemoveAvatar () {
      this.editingListing.avatar = ''
    }
  }
}
</script>

<style scoped>
.listing-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
}
@container (width < 900px) {
  .listing-section {
    grid-template-columns: 1fr;
    row-gap: 20px;
  }
}

.checkbox-input {
  border: 1px;
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.13), 0px 1px 2px 0px rgba(9, 25, 72, 0.13);
  outline: none;
  -webkit-tap-highlight-color: transparent;
}
.checkbox-input:focus,
.checkbox-input:focus-visible,
.checkbox-input:active {
  outline: none;
  box-shadow: 0px 0px 0px 1px rgba(9, 25, 72, 0.13), 0px 1px 2px 0px rgba(9, 25, 72, 0.13);
}

@property --submit-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --submit-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
.submit-btn {
  --submit-color: #121212;
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--submit-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--submit-btn-opacity-2)) 100%), var(--submit-color);
  color: white;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px var(--submit-color);
  transition: box-shadow 100ms ease-in-out,
    color 100ms ease-in-out,
    background-color 100ms ease-in-out,
    --submit-btn-opacity-1 100ms ease-in-out,
    --submit-btn-opacity-2 100ms ease-in-out,
    --submit-color 100ms ease-in-out;
}
.submit-btn.enabled:hover {
  --submit-btn-opacity-1: 0.24;
  --submit-btn-opacity-2: 0;
}
.submit-btn.disabled {
  color: #A4ABB8;
  box-shadow: none;
  --submit-btn-opacity-1: 0;
  --submit-btn-opacity-2: 0;
  --submit-color: #F6F8FA;
}

.secondary-btn {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  background-color: transparent;
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.secondary-btn:hover {
  box-shadow: none;
  background-color: #F6F8FA;
}

.fade-enter-active, .fade-leave-active { transition: opacity 150ms ease-in-out; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.fade-enter-to, .fade-leave-from { opacity: 1; }
</style>